import React from "react";
import { useTranslation } from "react-i18next";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import {
  Button,
  Flex,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";

export const Pagination = (props) => {
  const { t } = useTranslation();
  const { tableInstance, onPageChange, maxVisiblePages = 2, pageDivisor = "..." } = props;

  const { pageCount, canNextPage, canPreviousPage } = tableInstance;
  const { pageIndex } = tableInstance.state;
  const gotoPage = onPageChange ?? tableInstance.gotoPage;
  const previousPage = onPageChange ? () => onPageChange(pageIndex - 1) : tableInstance.previousPage;
  const nextPage = onPageChange ? () => onPageChange(pageIndex + 1) : tableInstance.nextPage;


  const createPages = (count) => {
    const pages = [];
    const currentPage = pageIndex + 1;

    if (count <= maxVisiblePages) {
      for (let i = 1; i <= count; i++) {
        pages.push(i);
      }
      return pages;
    }

    let startPage = 1;
    let endPage = count;

    if (currentPage <= Math.ceil(maxVisiblePages / 2)) {
      endPage = maxVisiblePages;
    } else if (currentPage >= count - Math.floor(maxVisiblePages / 2)) {
      startPage = count - maxVisiblePages + 1;
    } else {
      startPage = currentPage - Math.floor(maxVisiblePages / 2);
      endPage = currentPage + Math.floor(maxVisiblePages / 2);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push(pageDivisor);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < count) {
      if (endPage < count - 1) {
        pages.push(pageDivisor);
      }
      pages.push(count);
    }

    return pages;
  }

  const handlePageClick = (page) => {
    if (page === pageDivisor || page === pageIndex + 1) return;
    gotoPage(page - 1);
  }

  return (
    <Stack direction="row" alignSelf="center" spacing={{ lg: "4px",  sm: "2px"}}>
      {pageCount !== maxVisiblePages && canPreviousPage && (
        <Tooltip label={t("system.labels.previous-page")}>
          <Button
            variant="no-effects"
            transition="all .5s ease"
            w={{ base: "30px", md: "40px" }}
            minW={{ base: "30px", md: "40px" }}
            h="40px"
            borderRadius="8px"
            bg="#fff"
            border="1px solid lightgray"
            color={useColorModeValue("gray.500","gray.400")}
            disabled={pageIndex === 0}
            display="flex"
            _hover={{ opacity: "0.7", borderColor: "gray.500" }}
            onClick={previousPage}>
            <Icon as={GrFormPrevious} w="16px" h="16px" color={useColorModeValue("gray.700","white")}/>
          </Button>
        </Tooltip>
      )}

      {
        createPages(pageCount).map((pageNumber, index) => {
          if (pageNumber === pageDivisor) {
            return (
              <Flex key={index} w="20px" h="40px" direction="column" justifyContent="flex-end" alignItems="center">
                <Text fontSize="sm">
                  {pageNumber}
                </Text>
              </Flex>
            );
          }

          return (
            <Button
              key={index}
              variant="no-effects"
              transition="all .5s ease"
              w={{ base: "30px", md: "40px" }}
              minW={{ base: "30px", md: "40px" }}
              h="40px"
              borderRadius="8px"
              bg={pageNumber !== pageDivisor && pageNumber === pageIndex + 1 ? "blue.500" : "#fff"}
              border={pageNumber !== pageDivisor && pageNumber === pageIndex + 1 ? "none" : "1px solid lightgray"}
              _hover={{ opacity: "0.7", borderColor: "gray.500", }}
              onClick={() => handlePageClick(pageNumber)}>
              <Text fontSize="sm" color={pageNumber !== pageDivisor && pageNumber === pageIndex + 1 ? "#fff" : "gray.600"}>
                {pageNumber}
              </Text>
            </Button>
          );
        })
      }

      {pageCount !== maxVisiblePages && canNextPage && (
        <Tooltip label={t("system.labels.next-page")}>
          <Button
            variant="no-effects"
            transition="all .5s ease"
            w={{ base: "30px", md: "40px" }}
            minW={{ base: "30px", md: "40px" }}
            h="40px"
            borderRadius="8px"
            bg="#fff"
            border="1px solid lightgray"
            display="flex"
            _hover={{ opacity: "0.7", borderColor: "gray.500", }}
            onClick={nextPage}>
            <Icon as={GrFormNext} w="16px" h="16px" color={useColorModeValue("gray.500","gray.400")} />
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
}
